import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

export default class extends Controller {
  static targets = [
    "overlay",
    "incoming",
    "outgoing",
    "active",
    "microphoneError",
    "microphoneErrorText",
    "callDebugLog",
    "name",
    "initials",
    "timer",
    "remoteAudio",
    "statusText",
    "muteButton"
  ]

  connect() {
    this.subscription = consumer.subscriptions.create("CallChannel", {
      received: (data) => this.handleMessage(data),
      connected: () => {
        this.connected = true
        this.requestCallStatus()
      },
      disconnected: () => {
        this.connected = false
        this.cleanupOnCableDisconnect()
      }
    })

    this.audioContext = null
    this.ringtoneInterval = null
    this.timerInterval = null
    this.pc = null
    this.localStream = null
    this.currentCallId = null
    this.currentConversationId = null
    this.isCaller = false
    this.muted = false
    this.answeredAt = null
    this.microphoneError = false
    this.iceCandidateBuffer = []
    this.wakeLock = null
    this.disconnectTimeout = null
    this.connected = false
    this.debugEnabled = this.initDebugMode()

    this.boundHandleVisibilityChange = () => this.handleVisibilityChange()
    document.addEventListener("visibilitychange", this.boundHandleVisibilityChange)
  }

  initDebugMode() {
    try {
      const url = new URL(window.location.href)
      if (url.searchParams.has("call_debug")) {
        const enabled = url.searchParams.get("call_debug") === "1"
        window.sessionStorage.setItem("call_debug", enabled ? "1" : "0")
      }
      const enabled = window.sessionStorage.getItem("call_debug") === "1"
      if (enabled && this.hasCallDebugLogTarget) {
        this.callDebugLogTarget.classList.remove("hidden")
      }
      return enabled
    } catch (error) {
      return false
    }
  }

  disconnect() {
    document.removeEventListener("visibilitychange", this.boundHandleVisibilityChange)
    this.releaseWakeLock()
    this.subscription?.unsubscribe()
    this.cleanup()
  }

  // ---- UI actions ----------------------------------------------------------

  startCall(event) {
    event.preventDefault()
    const button = event.currentTarget
    const conversationId = button.dataset.conversationId
    if (!conversationId) return

    if (this.currentCallId) return

    this.currentConversationId = parseInt(conversationId, 10)
    this.isCaller = true
    this.log("[CALL] startCall", { conversationId: this.currentConversationId, role: "caller" })
    this.perform("start", { conversation_id: this.currentConversationId })
    this.showOutgoing("Вызов…")
    this.startRingtone("outgoing")
  }

  async acceptCall(event) {
    event.preventDefault()
    if (!this.currentCallId) return

    this.stopRingtone()
    this.log("[CALL] acceptCall: initializing callee peer connection before accepting", { callId: this.currentCallId })

    try {
      await this.initCalleePeerConnection()
      this.log("[CALL] acceptCall: peer connection ready, sending accept", { callId: this.currentCallId })
      this.perform("accept", { call_id: this.currentCallId })
    } catch (error) {
      this.error("[CALL] acceptCall: failed to initialize callee peer connection", error)
      this.handleMicrophoneError(error)
    }
  }

  declineCall(event) {
    event.preventDefault()
    const callId = this.currentCallId
    if (!callId) return

    try {
      this.stopRingtone()
      this.cleanup()
    } finally {
      this.perform("decline", { call_id: callId })
    }
  }

  cancelCall(event) {
    event.preventDefault()
    const callId = this.currentCallId
    if (!callId) return

    try {
      this.stopRingtone()
      this.cleanup()
    } finally {
      this.perform("cancel", { call_id: callId })
    }
  }

  endCall(event) {
    event.preventDefault()
    const callId = this.currentCallId
    if (!callId) return

    try {
      this.cleanup()
    } finally {
      this.perform("end", { call_id: callId })
    }
  }

  toggleMute(event) {
    event.preventDefault()
    this.muted = !this.muted
    if (this.localStream) {
      this.localStream.getAudioTracks().forEach((track) => { track.enabled = !this.muted })
    }
    if (this.hasMuteButtonTarget) {
      this.muteButtonTarget.textContent = this.muted ? "Вкл. микрофон" : "Выключить микрофон"
      this.muteButtonTarget.classList.toggle("bg-gray-500", this.muted)
      this.muteButtonTarget.classList.toggle("bg-white", !this.muted)
    }
  }

  // ---- Channel messages ----------------------------------------------------

  handleMessage(data) {
    switch (data.action) {
      case "outgoing":
        this.handleOutgoing(data)
        break
      case "incoming":
        this.handleIncoming(data)
        break
      case "accepted":
        this.handleAccepted(data)
        break
      case "ended":
        this.handleEnded(data)
        break
      case "signal":
        this.handleSignal(data)
        break
      case "rejected":
        this.handleRejected(data)
        break
      case "status":
        this.handleStatus(data)
        break
    }
  }

  handleOutgoing(data) {
    this.currentCallId = data.call_id
    this.currentConversationId = data.conversation_id
    this.log("[CALL] handleOutgoing", { callId: this.currentCallId, role: "caller" })
    this.setUser(data.user)
    this.showOutgoing("Вызов…")
  }

  handleIncoming(data) {
    if (this.currentCallId) return

    this.currentCallId = data.call_id
    this.currentConversationId = data.conversation_id
    this.isCaller = false
    this.log("[CALL] handleIncoming", { callId: this.currentCallId, role: "callee" })
    this.setUser(data.user)
    this.showIncoming()
    this.startRingtone("incoming")
  }

  async handleAccepted(data) {
    this.answeredAt = Date.now()
    this.stopRingtone()
    this.showActive()
    this.startTimer()
    this.log("[CALL] handleAccepted", { callId: this.currentCallId, isCaller: this.isCaller, hasPc: !!this.pc })

    if (this.isCaller) {
      if (this.pc) {
        this.warn("[CALL] handleAccepted: caller already has peer connection, skipping offer creation")
        return
      }
      this.log("[CALL] handleAccepted: caller creating offer now")
      await this.initCallerPeerConnection()
    } else {
      if (!this.pc) {
        this.error("[CALL] handleAccepted: callee has no peer connection")
        this.handleEnded({ status: "ended" })
        return
      }
      this.log("[CALL] handleAccepted: callee peer connection ready, waiting for offer")
    }
  }

  handleEnded(data) {
    this.log("[CALL] handleEnded triggered", { status: data?.status, duration: data?.duration, currentCallId: this.currentCallId })

    if (!this.currentCallId) {
      this.log("[CALL] handleEnded: already cleaned up locally, ignoring server echo")
      return
    }

    // Hide UI immediately first; media cleanup runs synchronously right after.
    this.hideOverlay()
    this.stopRingtone()
    this.cleanup()
  }

  handleRejected(data) {
    this.stopRingtone()
    this.cleanup()

    if (data.reason) {
      this.showStatus(data.reason)
      setTimeout(() => this.hideOverlay(), 1500)
    } else {
      this.hideOverlay()
    }
  }

  handleStatus(data) {
    if (!this.currentCallId || this.currentCallId !== data.call_id) return

    const terminalStatuses = [ "ended", "declined", "missed", "cancelled" ]
    if (terminalStatuses.includes(data.status)) {
      this.log("[CALL] handleStatus: call already finished, closing UI", { status: data.status })
      this.handleEnded({ status: data.status })
    } else {
      this.log("[CALL] handleStatus: call still active", { status: data.status })
    }
  }

  async handleSignal(data) {
    const payload = data.payload
    this.log("[CALL] handleSignal received", { type: payload?.type, hasPc: !!this.pc })

    if (payload.type === "offer") {
      if (!this.pc) {
        this.error("[CALL] handleSignal: received offer but peer connection is not initialized")
        return
      }

      try {
        await this.pc.setRemoteDescription(new RTCSessionDescription(payload))
        this.log("[CALL] setRemoteDescription offer ok")
        const answer = await this.pc.createAnswer()
        this.log("[CALL] createAnswer ok")
        await this.pc.setLocalDescription(answer)
        this.log("[CALL] setLocalDescription answer ok, sending answer")
        this.perform("signal", { call_id: this.currentCallId, payload: { type: "answer", sdp: answer.sdp } })
        await this.drainIceCandidateBuffer()
      } catch (error) {
        this.error("[CALL] handleSignal offer error:", error)
        this.handleEnded({ status: "ended" })
      }
      return
    }

    if (payload.type === "answer") {
      if (!this.pc) {
        this.error("[CALL] handleSignal: received answer but peer connection is not initialized")
        return
      }

      try {
        await this.pc.setRemoteDescription(new RTCSessionDescription(payload))
        this.log("[CALL] setRemoteDescription answer ok")
        await this.drainIceCandidateBuffer()
      } catch (error) {
        this.error("[CALL] handleSignal answer error:", error)
        this.handleEnded({ status: "ended" })
      }
      return
    }

    if (payload.type === "ice" && payload.candidate) {
      if (!this.pc) {
        this.log("[CALL] handleSignal: buffering ICE candidate (no peer connection yet)", { candidateType: payload.candidate.candidateType })
        this.iceCandidateBuffer.push(payload.candidate)
        return
      }

      try {
        await this.pc.addIceCandidate(new RTCIceCandidate(payload.candidate))
        this.log("[CALL] addIceCandidate ok", { candidateType: payload.candidate.candidateType })
      } catch (error) {
        this.error("[CALL] handleSignal ICE error:", error)
      }
      return
    }
  }

  async drainIceCandidateBuffer() {
    if (this.iceCandidateBuffer.length === 0) return

    this.log("[CALL] draining ICE candidate buffer", { count: this.iceCandidateBuffer.length })
    const buffer = this.iceCandidateBuffer
    this.iceCandidateBuffer = []

    for (const candidate of buffer) {
      try {
        await this.pc.addIceCandidate(new RTCIceCandidate(candidate))
        this.log("[CALL] buffered addIceCandidate ok", { candidateType: candidate.candidateType })
      } catch (error) {
        this.error("[CALL] buffered addIceCandidate error:", error)
      }
    }
  }

  // ---- WebRTC --------------------------------------------------------------

  async initCallerPeerConnection() {
    try {
      const iceServers = await this.fetchIceServers()
      this.log("[CALL] caller ICE servers", { count: iceServers.length, urls: iceServers.map((s) => s.urls) })
      await this.startLocalStream()

      this.pc = new RTCPeerConnection({ iceServers })
      this.attachLocalTracks()
      this.attachHandlers()

      const offer = await this.pc.createOffer()
      this.log("[CALL] createOffer ok")
      await this.pc.setLocalDescription(offer)
      this.log("[CALL] setLocalDescription offer ok, sending offer")
      this.perform("signal", { call_id: this.currentCallId, payload: { type: "offer", sdp: offer.sdp } })
    } catch (error) {
      this.error("[Call] init caller error:", error)
      this.handleMicrophoneError(error)
    }
  }

  async initCalleePeerConnection() {
    try {
      const iceServers = await this.fetchIceServers()
      this.log("[CALL] callee ICE servers", { count: iceServers.length, urls: iceServers.map((s) => s.urls) })
      await this.startLocalStream()

      this.pc = new RTCPeerConnection({ iceServers })
      this.attachLocalTracks()
      this.attachHandlers()
      this.log("[CALL] callee peer connection initialized")
    } catch (error) {
      this.error("[Call] init callee error:", error)
      this.handleMicrophoneError(error)
    }
  }

  attachLocalTracks() {
    if (!this.pc || !this.localStream) return
    this.localStream.getTracks().forEach((track) => this.pc.addTrack(track, this.localStream))
    this.log("[CALL] attachLocalTracks", { trackCount: this.localStream.getTracks().length })
  }

  attachHandlers() {
    this.pc.onicecandidate = (event) => {
      if (event.candidate) {
        this.log("[CALL] onicecandidate", { type: event.candidate.type, protocol: event.candidate.protocol })
        this.perform("signal", {
          call_id: this.currentCallId,
          payload: { type: "ice", candidate: event.candidate.toJSON() }
        })
      } else {
        this.log("[CALL] onicecandidate: end of candidates")
      }
    }

    this.pc.ontrack = (event) => {
      const tracks = event.streams[0]?.getTracks() || []
      this.log("[CALL] ontrack", { kind: event.track?.kind, trackCount: tracks.length })
      if (this.hasRemoteAudioTarget) {
        this.remoteAudioTarget.srcObject = event.streams[0]
        this.log("[CALL] remoteAudio srcObject assigned")
        this.remoteAudioTarget.play().then(() => {
          this.log("[CALL] remoteAudio play resolved")
        }).catch((error) => {
          this.warn("[CALL] remoteAudio play rejected:", error.name)
        })
      }
    }

    this.pc.oniceconnectionstatechange = () => {
      const state = this.pc.iceConnectionState
      this.log("[CALL] oniceconnectionstatechange", { state })
      if (state === "failed") {
        this.log("[CALL] ICE failed, ending call")
        this.notifyServerOfLocalEnd()
        this.handleEnded({ status: "ended" })
      }
    }

    this.pc.onconnectionstatechange = () => {
      const state = this.pc.connectionState
      this.log("[CALL] onconnectionstatechange", { state })
      if (state === "failed") {
        this.clearDisconnectTimeout()
        this.log("[CALL] connection failed, ending call")
        this.notifyServerOfLocalEnd()
        this.handleEnded({ status: "ended" })
      } else if (state === "disconnected") {
        if (this.hasStatusTextTarget) this.statusTextTarget.textContent = "Соединение…"
        this.clearDisconnectTimeout()
        this.disconnectTimeout = setTimeout(() => {
          this.log("[CALL] disconnected timeout, ending call")
          this.notifyServerOfLocalEnd()
          this.handleEnded({ status: "ended" })
        }, 5000)
      } else if (state === "connected") {
        this.clearDisconnectTimeout()
        if (this.hasStatusTextTarget) this.statusTextTarget.textContent = ""
      }
    }
  }

  async fetchIceServers() {
    const response = await fetch("/turn_credentials", {
      headers: { "Accept": "application/json" }
    })
    if (!response.ok) throw new Error("ICE servers unavailable")
    const data = await response.json()
    return data.iceServers
  }

  async startLocalStream() {
    try {
      this.localStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      })
      this.log("[CALL] getUserMedia success", { trackCount: this.localStream.getTracks().length })
    } catch (error) {
      this.error("[CALL] getUserMedia failed:", error.name)
      throw error
    }
  }

  // ---- Helpers -------------------------------------------------------------

  log(...args) {
    if (this.debugEnabled) {
      console.log(...args)
    }
    this.appendDebugLog("log", args)
  }

  error(...args) {
    console.error(...args)
    this.appendDebugLog("error", args)
  }

  warn(...args) {
    console.warn(...args)
    this.appendDebugLog("warn", args)
  }

  appendDebugLog(level, args) {
    if (!this.debugEnabled || !this.hasCallDebugLogTarget) return

    const line = document.createElement("div")
    line.className = level === "error" ? "text-red-400" : level === "warn" ? "text-yellow-400" : "text-green-400"

    const timestamp = new Date().toLocaleTimeString("ru-RU", { hour12: false })
    const text = args.map((arg) => {
      if (arg === null || arg === undefined) return String(arg)
      if (typeof arg === "object") {
        try {
          return JSON.stringify(arg)
        } catch (e) {
          return "[object]"
        }
      }
      return String(arg)
    }).join(" ")

    line.textContent = `${timestamp} ${text}`
    this.callDebugLogTarget.appendChild(line)

    while (this.callDebugLogTarget.children.length > 200) {
      this.callDebugLogTarget.removeChild(this.callDebugLogTarget.firstChild)
    }

    this.callDebugLogTarget.scrollTop = this.callDebugLogTarget.scrollHeight
  }

  perform(action, data) {
    if (this.subscription) {
      this.subscription.perform(action, data)
    }
  }

  notifyServerOfLocalEnd() {
    const callId = this.currentCallId
    if (!callId) return

    if (this.isCaller) {
      this.perform(this.answeredAt ? "end" : "cancel", { call_id: callId })
    } else {
      this.perform(this.answeredAt ? "end" : "decline", { call_id: callId })
    }
  }

  requestCallStatus() {
    if (this.currentCallId && this.connected) {
      this.log("[CALL] requesting call status", { callId: this.currentCallId })
      this.perform("status", { call_id: this.currentCallId })
    }
  }

  clearDisconnectTimeout() {
    if (this.disconnectTimeout) {
      clearTimeout(this.disconnectTimeout)
      this.disconnectTimeout = null
    }
  }

  cleanupOnCableDisconnect() {
    if (!this.currentCallId) return
    if (this.answeredAt) return

    this.log("[CALL] cable disconnected while ringing, cleaning up locally without notifying server")
    this.cleanup()
  }

  setUser(user) {
    if (this.hasNameTarget) this.nameTarget.textContent = user.name
    if (this.hasInitialsTarget) this.initialsTarget.textContent = user.initials
  }

  showOverlay() {
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.remove("hidden")
    }
  }

  hideOverlay() {
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.add("hidden")
    }
  }

  hideAllPanels() {
    if (this.hasIncomingTarget) this.incomingTarget.classList.add("hidden")
    if (this.hasOutgoingTarget) this.outgoingTarget.classList.add("hidden")
    if (this.hasActiveTarget) this.activeTarget.classList.add("hidden")
    if (this.hasMicrophoneErrorTarget) this.microphoneErrorTarget.classList.add("hidden")
  }

  showIncoming() {
    this.showOverlay()
    this.hideAllPanels()
    if (this.hasIncomingTarget) this.incomingTarget.classList.remove("hidden")
    this.requestWakeLock()
  }

  showOutgoing(statusText) {
    this.showOverlay()
    this.hideAllPanels()
    if (this.hasOutgoingTarget) this.outgoingTarget.classList.remove("hidden")
    if (this.hasStatusTextTarget) this.statusTextTarget.textContent = statusText || ""
    this.requestWakeLock()
  }

  showActive() {
    this.showOverlay()
    this.hideAllPanels()
    if (this.hasActiveTarget) this.activeTarget.classList.remove("hidden")
    this.requestWakeLock()
  }

  showStatus(text) {
    this.showOverlay()
    this.hideAllPanels()
    if (this.hasStatusTextTarget) this.statusTextTarget.textContent = text
  }

  handleMicrophoneError(error) {
    const errorName = error?.name || "UnknownError"
    const message = this.microphoneErrorMessage(errorName)
    this.log("[CALL] handleMicrophoneError", { errorName, message, callId: this.currentCallId, isCaller: this.isCaller })

    this.microphoneError = true
    this.stopRingtone()

    this.showOverlay()
    this.hideAllPanels()
    if (this.hasMicrophoneErrorTextTarget) {
      this.microphoneErrorTextTarget.textContent = message
    }
    if (this.hasMicrophoneErrorTarget) {
      this.microphoneErrorTarget.classList.remove("hidden")
    }

    const callId = this.currentCallId
    const isCaller = this.isCaller
    const wasAnswered = !!this.answeredAt

    this.cleanup()

    if (callId) {
      if (isCaller) {
        if (wasAnswered) {
          this.perform("end", { call_id: callId })
        } else {
          this.perform("cancel", { call_id: callId })
        }
      } else {
        if (wasAnswered) {
          this.perform("end", { call_id: callId })
        } else {
          this.perform("decline", { call_id: callId })
        }
      }
    }
  }

  microphoneErrorMessage(errorName) {
    switch (errorName) {
      case "NotFoundError":
        return "Микрофон не найден на этом устройстве"
      case "NotAllowedError":
        return "Нет доступа к микрофону — разрешите в настройках браузера"
      case "NotReadableError":
        return "Микрофон занят другим приложением"
      default:
        return "Ошибка микрофона"
    }
  }

  dismissMicrophoneError() {
    this.cleanup()
  }

  startTimer() {
    this.stopTimer()
    if (!this.hasTimerTarget) return

    const start = this.answeredAt || Date.now()
    const update = () => {
      const seconds = Math.floor((Date.now() - start) / 1000)
      const mins = Math.floor(seconds / 60)
      const secs = seconds % 60
      this.timerTarget.textContent = `${mins}:${secs.toString().padStart(2, "0")}`
    }
    update()
    this.timerInterval = setInterval(update, 1000)
  }

  stopTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval)
      this.timerInterval = null
    }
  }

  cleanup() {
    this.stopTimer()
    this.stopRingtone()
    this.clearDisconnectTimeout()

    if (this.pc) {
      this.pc.close()
      this.pc = null
    }

    if (this.localStream) {
      this.localStream.getTracks().forEach((track) => track.stop())
      this.localStream = null
    }

    if (this.hasRemoteAudioTarget) {
      this.remoteAudioTarget.srcObject = null
    }

    this.currentCallId = null
    this.currentConversationId = null
    this.isCaller = false
    this.muted = false
    this.answeredAt = null
    this.microphoneError = false
    this.iceCandidateBuffer = []
    this.releaseWakeLock()

    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.add("hidden")
    }
  }

  // ---- Wake Lock -----------------------------------------------------------

  async requestWakeLock() {
    if (!navigator.wakeLock || this.wakeLock) return

    try {
      this.wakeLock = await navigator.wakeLock.request("screen")
      this.log("[CALL] wake lock acquired")
      this.wakeLock.addEventListener("release", () => {
        this.log("[CALL] wake lock released")
        this.wakeLock = null
      })
    } catch (error) {
      this.log("[CALL] wake lock request failed", { name: error.name })
    }
  }

  releaseWakeLock() {
    if (!this.wakeLock) return

    this.wakeLock.release()
    this.wakeLock = null
  }

  handleVisibilityChange() {
    if (document.visibilityState !== "visible") return

    if (this.currentCallId) {
      this.log("[CALL] visibility changed to visible, checking call status")
      this.requestCallStatus()
    }

    if (this.currentCallId && !this.wakeLock) {
      this.log("[CALL] re-requesting wake lock")
      this.requestWakeLock()
    }
  }

  // ---- Sound ---------------------------------------------------------------

  ensureAudioContext() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)()
    }
    if (this.audioContext.state === "suspended") {
      this.audioContext.resume()
    }
  }

  startRingtone(type) {
    this.ensureAudioContext()
    this.stopRingtone()

    const playTone = () => {
      if (!this.audioContext) return
      const oscillator = this.audioContext.createOscillator()
      const gain = this.audioContext.createGain()
      oscillator.connect(gain)
      gain.connect(this.audioContext.destination)

      if (type === "incoming") {
        oscillator.type = "sine"
        oscillator.frequency.setValueAtTime(440, this.audioContext.currentTime)
        oscillator.frequency.setValueAtTime(880, this.audioContext.currentTime + 0.2)
        gain.gain.setValueAtTime(0.3, this.audioContext.currentTime)
        gain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 1.5)
        oscillator.start()
        oscillator.stop(this.audioContext.currentTime + 1.5)
      } else {
        oscillator.type = "sine"
        oscillator.frequency.setValueAtTime(440, this.audioContext.currentTime)
        gain.gain.setValueAtTime(0.15, this.audioContext.currentTime)
        gain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.2)
        oscillator.start()
        oscillator.stop(this.audioContext.currentTime + 0.2)
      }
    }

    playTone()
    this.ringtoneInterval = setInterval(playTone, type === "incoming" ? 3000 : 2000)
  }

  stopRingtone() {
    if (this.ringtoneInterval) {
      clearInterval(this.ringtoneInterval)
      this.ringtoneInterval = null
    }
  }
}
